class HomeTimeField extends HTMLElement {
  /**
   * Mirrors the same-day cutoff logic used in the delivery countdown
   * (blocks/price.liquid): Tue-Thu cutoff 15:00, Fri cutoff 13:00.
   * Returns the weekday (0-6) of the day the delivery will actually
   * happen on - today if we're still before today's cutoff, otherwise
   * the next available delivery day.
   */
  static getRelevantDeliveryWeekday() {
    var DELIVERY_DAYS = [2, 3, 4, 5]; // Tuesday - Friday
    var FRIDAY = 5;

    function getCutoffHour(weekday) {
      return weekday === FRIDAY ? 13 : 15;
    }

    var now = new Date();
    var weekday = now.getDay();
    var cutoff = new Date(now);
    cutoff.setHours(getCutoffHour(weekday), 0, 0, 0);

    if (DELIVERY_DAYS.indexOf(weekday) !== -1 && now < cutoff) {
      return weekday;
    }

    var nextDay = new Date(now);
    do {
      nextDay.setDate(nextDay.getDate() + 1);
    } while (DELIVERY_DAYS.indexOf(nextDay.getDay()) === -1);

    return nextDay.getDay();
  }

  /**
   * Pickup windows per weekday (Tue-Fri), always the 30 minutes before the delivery window.
   * Same-day pickup uses the delivery order cutoff (Tue-Thu 15:00, Fri 13:00), 30 minutes before the window.
   */
  static PICKUP_WINDOWS = {
    2: { start: [15, 30], end: [16, 0] },
    3: { start: [15, 30], end: [16, 0] },
    4: { start: [15, 30], end: [16, 0] },
    5: { start: [13, 30], end: [14, 0] },
  };

  /** Current wall-clock time in Vienna, independent of the visitor's time zone. */
  static getViennaNow() {
    var parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Vienna',
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
      .formatToParts(new Date())
      .forEach(function (part) {
        parts[part.type] = part.value;
      });

    return new Date(
      parseInt(parts.year, 10),
      parseInt(parts.month, 10) - 1,
      parseInt(parts.day, 10),
      parts.hour === '24' ? 0 : parseInt(parts.hour, 10),
      parseInt(parts.minute, 10)
    );
  }

  /** The next five pickup dates (Tue-Fri) in chronological order. */
  static getPickupDays() {
    var windows = HomeTimeField.PICKUP_WINDOWS;
    var now = HomeTimeField.getViennaNow();
    var day = new Date(now);
    var days = [];

    while (days.length < 5) {
      var slot = windows[day.getDay()];
      if (slot) {
        var lastOrder = new Date(day);
        lastOrder.setHours(slot.start[0], slot.start[1] - 30, 0, 0);
        if (day.toDateString() !== now.toDateString() || now < lastOrder) {
          days.push(new Date(day));
        }
      }
      day.setDate(day.getDate() + 1);
    }

    return days;
  }

  connectedCallback() {
    this.#populate();
    if (typeof MinOrder !== 'undefined') MinOrder.updateProduct(this);
    this.addEventListener('change', this.#onModeChange);

    if (this.#observer) return;
    this.#observer = new MutationObserver(() => this.#populate());
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onModeChange);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #observer = null;

  get #isPickup() {
    var checked = this.querySelector('.cart-home-time-field__modes input:checked');
    return checked ? checked.value === 'Abholung' : false;
  }

  /**
   * Switching between delivery and pickup swaps the label, hint and options of the select.
   * Every choice is saved on the cart right away so the first cart line can show it in checkout.
   */
  #onModeChange = (event) => {
    var hidden = this.querySelector('input[name="attributes[lieferart]"]');

    if (event.target instanceof HTMLSelectElement) {
      OrderLineInfo.save(OrderLineInfo.timeAttributes(hidden ? hidden.value : 'Lieferung', event.target.value));
      return;
    }

    var radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;

    var isPickup = this.#isPickup;
    var label = this.querySelector('.cart-home-time-field__label');
    var hint = this.querySelector('.cart-home-time-field__hint');
    var select = this.querySelector('select');

    if (hidden) hidden.value = isPickup ? 'Abholung' : 'Lieferung';
    if (label) label.textContent = isPickup ? this.dataset.labelPickup : this.dataset.labelDelivery;
    if (hint) hint.hidden = !isPickup;
    if (!select) return;

    select.name = OrderLineInfo.fieldName(isPickup ? 'Abholung' : 'Lieferung');
    this.dataset.existingValue = '';
    while (select.options.length > 1) select.remove(1);
    select.selectedIndex = 0;
    this.#populate();

    OrderLineInfo.save(OrderLineInfo.timeAttributes(isPickup ? 'Abholung' : 'Lieferung', ''));
  };

  #populate() {
    var select = this.querySelector('select');
    if (!select) return;

    // Only the placeholder option means the server re-rendered/reset this field
    // (e.g. after a cart update morph) and the weekday options need to be re-added.
    if (select.options.length > 1) return;

    var isEn = this.dataset.locale === 'en';
    var isEs = this.dataset.locale === 'es';
    var existingValue = this.dataset.existingValue;

    if (this.#isPickup) {
      var DAY_NAMES = isEn
        ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        : isEs
          ? ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
          : ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

      function formatTime(time) {
        var minutes = (time[1] < 10 ? '0' : '') + time[1];
        if (!isEn) return time[0] + ':' + minutes;
        var hour12 = time[0] % 12 === 0 ? 12 : time[0] % 12;
        return hour12 + ':' + minutes;
      }

      var MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

      HomeTimeField.getPickupDays().forEach(function (date) {
        var weekday = date.getDay();
        var slot = HomeTimeField.PICKUP_WINDOWS[weekday];
        var range = formatTime(slot.start) + '–' + formatTime(slot.end);
        var day = date.getDate();
        var month = date.getMonth() + 1;
        // e.g. "Mittwoch, 14.10. · 15:30–16:00 Uhr" / "Wednesday, Oct 14 · 3:30–4:00 PM" / "Miércoles 14/10 · 15:30–16:00"
        var label = isEn
          ? DAY_NAMES[weekday] + ', ' + MONTHS_EN[month - 1] + ' ' + day + ' · ' + range + ' PM'
          : isEs
            ? DAY_NAMES[weekday] + ' ' + day + '/' + month + ' · ' + range
            : DAY_NAMES[weekday] + ', ' + day + '.' + month + '. · ' + range + ' Uhr';
        var option = document.createElement('option');
        option.value = label;
        option.textContent = label;
        if (existingValue && existingValue === label) {
          option.selected = true;
        }
        select.appendChild(option);
      });
      return;
    }

    var relevantWeekday = HomeTimeField.getRelevantDeliveryWeekday();
    var FRIDAY = 5;
    var hours = relevantWeekday === FRIDAY ? [14, 15, 16, 17] : [16, 17, 18, 19];

    function formatHourEn(hour) {
      var suffix = hour >= 12 ? 'PM' : 'AM';
      var hour12 = hour % 12 === 0 ? 12 : hour % 12;
      return hour12 + ' ' + suffix;
    }

    hours.forEach(function (hour) {
      var label = isEn
        ? 'Home from ' + formatHourEn(hour)
        : isEs
          ? 'En casa desde las ' + hour + ':00'
          : 'Ab ' + hour + ' Uhr zuhause';
      var option = document.createElement('option');
      option.value = label;
      option.textContent = label;
      if (existingValue && existingValue === label) {
        option.selected = true;
      }
      select.appendChild(option);
    });
  }
}

if (!customElements.get('home-time-field')) {
  customElements.define('home-time-field', HomeTimeField);
}

/**
 * Shows the chosen delivery time / pickup day once, as a line item property on the first cart
 * line (e.g. "Abholung: Mittwoch, 14.10. · 15:30–16:00 Uhr"). Shopify lists line item properties
 * under the product in the checkout order summary, the order confirmation and the admin order.
 * All other lines are kept free of it, so the info moves along if the first product is removed.
 */
var OrderLineInfo = {
  SOURCE_ID: 'order-line-info',

  /**
   * Cart attributes for the chosen time: delivery stores "ab_wann_zuhause", pickup stores "abholtag".
   * The other one is cleared (empty string removes it), so the order only shows the matching field.
   */
  timeAttributes: function (lieferart, value) {
    var isPickup = lieferart === 'Abholung';
    return {
      lieferart: isPickup ? 'Abholung' : 'Lieferung',
      ab_wann_zuhause: isPickup ? '' : value || '',
      abholtag: isPickup ? value || '' : '',
    };
  },

  /** Form field name of the time select for the chosen mode. */
  fieldName: function (lieferart) {
    return lieferart === 'Abholung' ? 'attributes[abholtag]' : 'attributes[ab_wann_zuhause]';
  },
  // Property names this script owns, in every shop language, so stale ones get cleaned up.
  MANAGED_KEYS: ['Lieferung', 'Abholung', 'Delivery', 'Pickup', 'Envío a domicilio', 'Retiro en local', 'Retiro'],
  running: null,
  rerun: false,

  get root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  },

  labels: function () {
    var field = document.querySelector('home-time-field[data-line-label-delivery]');
    return {
      Lieferung: (field && field.dataset.lineLabelDelivery) || 'Lieferung',
      Abholung: (field && field.dataset.lineLabelPickup) || 'Abholung',
    };
  },

  save: function (attributes) {
    return fetch(OrderLineInfo.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
    })
      .catch(function () {})
      .then(function () {
        return OrderLineInfo.sync();
      });
  },

  lastPreference: null,

  /**
   * Tells Shopify which delivery method the customer picked in the shop, so checkout opens on the
   * matching tab (Delivery / Abholung) instead of the one clicked in an earlier checkout visit.
   * Uses the tokenless Storefront API cart preference, which checkout uses to prefill.
   */
  setDeliveryPreference: function (cart) {
    var lieferart = (cart.attributes || {}).lieferart;
    if (!cart.token || !cart.item_count || (lieferart !== 'Abholung' && lieferart !== 'Lieferung')) {
      return Promise.resolve();
    }

    var method = lieferart === 'Abholung' ? 'PICK_UP' : 'SHIPPING';
    var marker = cart.token + ':' + method;
    if (OrderLineInfo.lastPreference === marker) return Promise.resolve();

    return fetch('/api/2025-07/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        query:
          'mutation($id: ID!, $buyer: CartBuyerIdentityInput!) { cartBuyerIdentityUpdate(cartId: $id, buyerIdentity: $buyer) { userErrors { message } } }',
        variables: {
          id: 'gid://shopify/Cart/' + cart.token,
          buyer: { preferences: { delivery: { deliveryMethod: [method] } } },
        },
      }),
    })
      .then(function (response) {
        if (response.ok) OrderLineInfo.lastPreference = marker;
      })
      .catch(function () {});
  },

  /** Serialised so overlapping triggers never edit the cart at the same time. */
  sync: function () {
    if (OrderLineInfo.running) {
      OrderLineInfo.rerun = true;
      return OrderLineInfo.running;
    }

    OrderLineInfo.running = OrderLineInfo.apply(0, false).finally(function () {
      OrderLineInfo.running = null;
      if (OrderLineInfo.rerun) {
        OrderLineInfo.rerun = false;
        OrderLineInfo.sync();
      }
    });
    return OrderLineInfo.running;
  },

  /** Fixes one line per pass (line numbers can shift after a change) until the cart matches. */
  apply: function (pass, changed) {
    var root = OrderLineInfo.root;

    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        var attributes = cart.attributes || {};
        var value =
          (attributes.lieferart === 'Abholung' ? attributes.abholtag || attributes.ab_wann_zuhause : attributes.ab_wann_zuhause) || '';
        var key = OrderLineInfo.labels()[attributes.lieferart === 'Abholung' ? 'Abholung' : 'Lieferung'];

        var fix = QuantityLimit.fix(cart);
        cart.items.some(function (item, index) {
          if (fix) return true;
          var current = item.properties || {};
          var wanted = {};
          var differs = false;
          Object.keys(current).forEach(function (name) {
            wanted[name] = current[name];
          });

          // Shopify ignores an empty properties object, so stale info is cleared by setting it to null
          // (null properties are hidden in cart and checkout).
          OrderLineInfo.MANAGED_KEYS.forEach(function (name) {
            var target = index === 0 && value && name === key ? value : null;
            if ((current[name] == null ? null : current[name]) !== target) {
              wanted[name] = target;
              differs = true;
            }
          });

          if (differs) {
            fix = { line: index + 1, quantity: item.quantity, properties: wanted };
            return true;
          }
          return false;
        });

        if (!fix || pass >= 10) {
          var preference = OrderLineInfo.setDeliveryPreference(cart);
          if (!changed) return preference;
          return Promise.all([preference, import('@theme/events')]).then(function (results) {
            var events = results[1];
            document.dispatchEvent(
              new events.CartUpdateEvent(cart, OrderLineInfo.SOURCE_ID, {
                source: OrderLineInfo.SOURCE_ID,
                itemCount: cart.item_count,
              })
            );
          });
        }

        return fetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(fix),
        }).then(function () {
          return OrderLineInfo.apply(pass + 1, true);
        });
      })
      .catch(function () {});
  },
};

document.addEventListener('cart:update', function (event) {
  if (event.detail && event.detail.sourceId === OrderLineInfo.SOURCE_ID) return;
  OrderLineInfo.sync();
});

// The add-to-cart popup (snippets/add-to-cart-popup.liquid) waits for the sync before opening the cart drawer.
window.OrderLineInfo = OrderLineInfo;

// The cart "Zur Kasse" button: make sure the info is on the first line before checkout opens.
document.addEventListener(
  'click',
  function (event) {
    var button = event.target instanceof Element && event.target.closest('#checkout');
    if (!button || button.dataset.lineInfoReady) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    var form = button.form || document.getElementById('cart-form');
    var field = document.querySelector('home-time-field');
    var select = field && field.querySelector('select');
    var hidden = field && field.querySelector('input[name="attributes[lieferart]"]');

    var ready =
      select && select.value
        ? OrderLineInfo.save(OrderLineInfo.timeAttributes(hidden ? hidden.value : 'Lieferung', select.value))
        : Promise.resolve();

    ready.then(function () {
      button.dataset.lineInfoReady = 'true';
      if (form && form.requestSubmit) form.requestSubmit(button);
      else button.click();
      delete button.dataset.lineInfoReady;
    });
  },
  true
);

OrderLineInfo.sync();

/**
 * "Weitere Bezahlmöglichkeiten" (more payment options) on the product page starts a checkout
 * for just this product and drops the cart attribute ab_wann_zuhause. Instead, add the product
 * to the cart together with the selected time and continue to checkout from the cart, so the
 * time always ends up on the order.
 */
document.addEventListener(
  'click',
  function (event) {
    var link = event.target instanceof Element && event.target.closest('.shopify-payment-button__more-options');
    if (!link) return;

    var form = link.closest('form');
    var select = form && form.querySelector('home-time-field select');
    if (!select) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!select.value) {
      select.reportValidity();
      return;
    }

    if (link.dataset.busy) return;
    link.dataset.busy = 'true';

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var headers = { Accept: 'application/json' };
    var mode = form.querySelector('home-time-field input[name="attributes[lieferart]"]');
    var attributes = OrderLineInfo.timeAttributes(mode ? mode.value : 'Lieferung', select.value);

    fetch(root + 'cart/add.js', { method: 'POST', headers: headers, body: new FormData(form) })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/add failed');
        return fetch(root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes }),
        });
      })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/update failed');
        return OrderLineInfo.sync();
      })
      .then(function () {
        return fetch(root + 'cart.js', { headers: headers }).then(function (response) {
          return response.json();
        });
      })
      .then(function (cart) {
        // Below the minimum order value the cart page explains what is missing instead.
        window.location.href = root + (cart.total_price < MinOrder.CENTS ? 'cart' : 'checkout');
      })
      .catch(function () {
        delete link.dataset.busy;
        // Fall back to the regular add-to-cart flow, which submits the time with the form.
        form.requestSubmit();
      });
  },
  true
);

/**
 * Maximum quantity per order for single products (all variants together). Shopify only offers
 * quantity rules for B2B on Plus, so the theme caps the cart; the cart shows a note (cart-summary.liquid).
 */
var QuantityLimit = {
  LIMITS: { 'entrana-skirt-steak': 3 },

  /** Returns a cart/change.js payload that lowers the last line over the limit, or null. */
  fix: function (cart) {
    var result = null;
    Object.keys(QuantityLimit.LIMITS).some(function (handle) {
      var excess = -QuantityLimit.LIMITS[handle];
      var last = -1;
      cart.items.forEach(function (item, index) {
        if (item.handle !== handle) return;
        excess += item.quantity;
        last = index;
      });
      if (excess <= 0) return false;

      result = { line: last + 1, quantity: Math.max(cart.items[last].quantity - excess, 0) };
      return true;
    });
    return result;
  },
};

/**
 * Minimum order value of 35 € for delivery and pickup. The cart blocks its checkout buttons in Liquid
 * (snippets/cart-summary.liquid); on the product page the express buttons (PayPal, Google Pay,
 * "Weitere Bezahlmöglichkeiten") buy only this product, so they are hidden while it stays below 35 €.
 */
var MinOrder = {
  CENTS: 3500,

  updateProduct: function (field) {
    if (!field.dataset.variantPrices) return;

    var form = field.closest('form');
    var block = form && form.querySelector('.accelerated-checkout-block');
    var note = field.querySelector('.cart-home-time-field__min-order');
    if (!form || !block) return;

    var prices = {};
    try {
      prices = JSON.parse(field.dataset.variantPrices);
    } catch (error) {
      return;
    }

    var idInput = form.querySelector('input[name="id"]');
    var quantityInput =
      form.querySelector('input[name="quantity"]') ||
      (form.id && document.querySelector('input[name="quantity"][form="' + form.id + '"]'));
    var price = idInput ? prices[idInput.value] : undefined;
    var quantity = (quantityInput && parseInt(quantityInput.value, 10)) || 1;

    var limit = QuantityLimit.LIMITS[field.dataset.productHandle];
    if (limit && quantityInput) {
      quantityInput.max = limit;
      if (quantity > limit) {
        quantity = limit;
        quantityInput.value = limit;
      }
    }

    var below = typeof price === 'number' && price * quantity < MinOrder.CENTS;
    block.style.display = below ? 'none' : '';
    if (note) note.hidden = !below;
  },

  updateAll: function () {
    document.querySelectorAll('home-time-field[data-variant-prices]').forEach(MinOrder.updateProduct);
  },
};

['variant:update', 'quantity-selector:update', 'change', 'input', 'click'].forEach(function (name) {
  document.addEventListener(name, function () {
    setTimeout(MinOrder.updateAll, 50);
  });
});

MinOrder.updateAll();
